/* eslint-disable react/no-array-index-key */
import React, { useState } from 'react';
import moment from 'moment';
import { Waypoint } from 'react-waypoint';
import times from 'lodash/times';
import { PrimaryButton } from '../CommonComponents/Button';
import { useActivities } from './api/useActivities';
import { UserActivityLoading } from './UserActivityLoading';
import { UserActivityItemLoading } from './components/UserActivityItem/UserActivityItemLoading';
import { UserActivityItem } from './components/UserActivityItem/UserActivityItem';
import { AddUserActivity } from './components/AddUserActivity/AddUserActivity';
import { ErrorState } from '../CommonComponents/ErrorState/ErrorState';
import {
  IsFetching,
  UserActivities,
  UserActivityDay,
  UserActivityDayTitle,
  UserActivityHeader,
  UserActivityPageTitle,
} from './UserActivityStyles';
import { draftColor } from './UserActivityHelpers';
import { useDocumentTitle } from '../_hooks/useDocumentTitle';
import { Toggle } from '../CommonComponents/OPL/Toggle/Toggle';
import { useFeatureFlag } from '../_hooks/useFeatureFlag';
import { Feature } from '../CommonSelectors/CommonArea.types';

interface Props {
  clientId: string;
}

export const UserActivity: React.FC<Props> = ({ clientId }) => {
  const [showAddActivityModal, setShowAddActivityModal] = useState(false);

  const showAutomaticAccountUpdatesToggle = useFeatureFlag(
    Feature.EnableHideAutomaticAccountUpdatesToggle,
  );

  const [hideAutomatic, setHideAutomatic] = useState(showAutomaticAccountUpdatesToggle);

  const {
    isLoading,
    isError,
    data,
    isFetching,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    failureCount,
    refetch,
  } = useActivities(clientId, hideAutomatic);

  useDocumentTitle('User Activity');

  if (isLoading) {
    return <UserActivityLoading />;
  }

  if (isError) {
    return <ErrorState onRetry={refetch} />;
  }

  if (!data) {
    return null;
  }

  return (
    <div>
      <UserActivityHeader>
        <UserActivityPageTitle>User Activity</UserActivityPageTitle>
        <PrimaryButton prependIcon="fa fa-plus" onClick={() => setShowAddActivityModal(true)}>
          Add Activity
        </PrimaryButton>

        {isFetching && <IsFetching />}
      </UserActivityHeader>

      {showAutomaticAccountUpdatesToggle && (
        <Toggle checked={hideAutomatic} onChange={ev => setHideAutomatic(ev.target.checked)}>
          Hide Automatic Account Updates
        </Toggle>
      )}

      {data.pages.map(({ Items }, i) => (
        <React.Fragment key={i}>
          {Items.map(({ Date, DayActivity }, j) => (
            <UserActivityDay key={j}>
              <UserActivityDayTitle>{moment(Date).format('MMMM DD, YYYY')}</UserActivityDayTitle>
              {DayActivity.map(({ Initial, Activities }, k) => (
                <UserActivities key={k}>
                  {Activities.map(activity => (
                    <UserActivityItem
                      key={activity.EventId}
                      activity={activity}
                      color={draftColor(Initial)}
                    />
                  ))}
                </UserActivities>
              ))}
            </UserActivityDay>
          ))}
        </React.Fragment>
      ))}

      {isFetchingNextPage || failureCount > 0 ? (
        <UserActivities>
          {times(3).map(key => (
            <UserActivityItemLoading key={key} />
          ))}
        </UserActivities>
      ) : (
        hasNextPage && <Waypoint onEnter={() => (fetchNextPage ? fetchNextPage() : {})} />
      )}

      {!hasNextPage && (
        <p style={{ textAlign: 'center', color: '#6C727F' }}>
          There are no further items available in this user&apos;s activity log.
        </p>
      )}

      {showAddActivityModal && (
        <AddUserActivity
          clientId={clientId}
          onRequestClose={() => setShowAddActivityModal(false)}
        />
      )}
    </div>
  );
};
